// src/pages/RegistroCommessePage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { Search, Briefcase, Printer } from "lucide-react";
import { isTaskOverdue } from "../utils/taskHelpers";
import { differenceInDays } from "date-fns";
import { formatValutaItaliana } from "../utils/clienteHelpers";
import { variantiDaBudget } from "../utils/economiaCommessa";
import { dominioSito } from "../utils/fornitore";

// Stessa identica logica di calcolo già usata in Centro Operativo e Suggerimenti Operativi
function calcHealthScore(tasks) {
  if (!tasks.length) return { score: null, label: "Non avviato", color: "#9ca3af", emoji: "⚪" };
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === "completata").length;
  const overdue = tasks.filter(t => isTaskOverdue(t)).length;
  const critical = tasks.filter(t => t.priority === "alta" && t.status !== "completata").length;
  const recent = tasks.filter(t => {
    const d = t.updatedAt?.toDate?.();
    return d && differenceInDays(new Date(), d) <= 7;
  }).length;
  let score = 100;
  score -= Math.min(40, overdue * 10);
  score -= Math.min(20, critical * 5);
  score -= total > 0 ? Math.max(0, 20 - Math.round(completed / total * 20)) : 0;
  score += recent > 0 ? Math.min(10, recent * 2) : -10;
  score = Math.max(0, Math.min(100, Math.round(score)));
  if (score >= 90) return { score, label: "Eccellente", color: "#059669", emoji: "🟢" };
  if (score >= 70) return { score, label: "Buono", color: "#2563eb", emoji: "🔵" };
  if (score >= 50) return { score, label: "Attenzione", color: "#d97706", emoji: "🟡" };
  return { score, label: "Critico", color: "#dc2626", emoji: "🔴" };
}

// Giorni dall'apertura: se ancora aperta, conta fino ad oggi (aumenta ogni giorno);
// se chiusa, conta fino alla data di chiusura (numero fisso, non cambia più).
function calcGiorniCommessa(c) {
  const apertura = c.createdAt?.toDate ? c.createdAt.toDate() : null;
  if (!apertura) return null;
  const fine = c.dataChiusura ? new Date(c.dataChiusura+"T00:00:00") : new Date();
  return Math.max(0, differenceInDays(fine, apertura));
}

const escN = (t) => String(t ?? "").replace(/[&<>"]/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));

export default function RegistroCommessePage({ onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const [commesse, setCommesse] = useState([]);
  const [tasksPerProgetto, setTasksPerProgetto] = useState({});
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)),
      snap => {
        const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(p => !p.isParent);
        lista.sort((a, b) => (b.codiceCommessa || "").localeCompare(a.codiceCommessa || "", "it", { numeric: true }));
        setCommesse(lista);
      }
    );
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "tasks"), where("organizationId", "==", effectiveOrgId)),
      snap => {
        const raggruppati = {};
        snap.docs.forEach(d => {
          const t = { id: d.id, ...d.data() };
          if (!t.projectId) return;
          (raggruppati[t.projectId] ||= []).push(t);
        });
        setTasksPerProgetto(raggruppati);
      }
    );
  }, [effectiveOrgId]);

  // Valore commessa (dato già presente in Budget Commessa — Controllo Economico — nessuna nuova informazione)
  const [budgetPerProgetto, setBudgetPerProgetto] = useState({});
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "budgetCommessa"), where("organizationId", "==", effectiveOrgId)),
      snap => {
        const mappa = {};
        // W452: con due budget per la stessa commessa vale il primo per identificativo, come nel Controllo Economico
        snap.docs.forEach(d => { const pid = d.data().projectId; if (pid && !mappa[pid]) mappa[pid] = d.data(); });
        setBudgetPerProgetto(mappa);
      }
    );
  }, [effectiveOrgId]);

  const filtrate = commesse.filter(c => {
    if (!cerca) return true;
    const s = cerca.toLowerCase();
    if ((c.numeroOrdine || "").toLowerCase().includes(s)) return true;
    return [c.codiceCommessa, c.clienteNome, c.name, c.description, c.responsabileNome]
      .some(v => (v || "").toLowerCase().includes(s));
  });

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const righe = filtrate.map(c => {
      const budget = budgetPerProgetto[c.id];
      const tasks = tasksPerProgetto[c.id] || [];
      const aperte = tasks.filter(t=>t.status!=="completata").length;
      const scadute = tasks.filter(t=>isTaskOverdue(t)).length;
      const health = calcHealthScore(tasks);
      const stato = c.archived ? "Archiviata" : (c.statoCommessa==="chiusa" ? "Chiusa" : "Attiva");
      const giorni = calcGiorniCommessa(c);
      return `<tr>
        <td style="font-weight:800;color:#1e1b4b">${escN(c.numeroOrdine)||"—"}</td>
        <td style="font-family:monospace;font-weight:700;color:#7c3aed">${c.codiceCommessa||"—"}</td>
        <td>${c.clienteNome||"—"}</td>
        <td>${c.description||c.name||"—"}</td>
        <td>${c.responsabileNome||"—"}</td>
        <td>${c.createdAt?.toDate ? c.createdAt.toDate().toLocaleDateString("it-IT") : "—"}</td>
        <td>${c.dataChiusura ? new Date(c.dataChiusura+"T00:00:00").toLocaleDateString("it-IT") : "—"}</td>
        <td style="text-align:center">${giorni===null?"—":giorni}</td>
        <td>${stato}</td>
        <td style="text-align:right">${budget?.importoCommessa ? "€ "+formatValutaItaliana(budget.importoCommessa) : "—"}${variantiDaBudget(budget)>0 ? `<br><span style="font-size:10px;color:#7c3aed">+ varianti € ${formatValutaItaliana(variantiDaBudget(budget))}</span>` : ""}</td>
        <td style="text-align:center">${aperte}${scadute>0?` <span style="color:#dc2626;font-weight:700">(${scadute} in ritardo)</span>`:""}</td>
        <td>${health.score===null?health.label:`${health.score} · ${health.label}`}</td>
      </tr>`;
    }).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Registro Commesse</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:10px;color:#111;padding:10mm 12mm;}
      @media screen{body{max-width:1150px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:14px;}
      .hdr div:first-child{font-size:16px;font-weight:800;color:#1e1b4b}
      .hdr div:last-child{font-size:10px;color:#6b7280;margin-top:2px}
      table{width:100%;border-collapse:collapse;}
      th{text-align:left;font-size:8px;color:#6b7280;text-transform:uppercase;letter-spacing:.03em;padding:5px 6px;border-bottom:2px solid #e5e7eb;background:#f9fafb;}
      td{padding:5px 6px;border-bottom:1px solid #f3f4f6;font-size:10px;}
      .footer{border-top:1px solid #e5e7eb;margin-top:14px;padding-top:6px;font-size:8px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>REGISTRO COMMESSE</div>
      <div>${filtrate.length} commesse — generato il ${new Date().toLocaleDateString("it-IT")}</div>
    </div>
    <table>
      <thead><tr>
        <th>N. ordine</th><th>Codice</th><th>Cliente</th><th>Descrizione / Oggetto</th><th>PM</th>
        <th>Apertura</th><th>Chiusura</th><th>Giorni</th><th>Stato</th><th>Valore</th><th>Attività aperte</th><th>Salute</th>
      </tr></thead>
      <tbody>${righe}</tbody>
    </table>
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, margin:0 }}>
            <Briefcase size={17} color="#7c3aed" /> Registro Commesse
          </h2>
          <button onClick={stampaPDF} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",border:"1px solid #7c3aed",borderRadius:8,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <Printer size={14}/> Stampa PDF
          </button>
        </div>
        <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
          {commesse.length} commesse totali · {commesse.filter(c=>!c.archived && c.statoCommessa!=="chiusa").length} attive · {commesse.filter(c=>!c.archived && c.statoCommessa==="chiusa").length} chiuse · {commesse.filter(c=>c.archived).length} archiviate
        </p>
      </div>

      <div style={{ padding: "12px 24px", borderBottom: "1px solid #e5e7eb", background: "#fff" }}>
        <div style={{ position: "relative", maxWidth: 360 }}>
          <Search size={14} color="#9ca3af" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input value={cerca} onChange={e => setCerca(e.target.value)}
            placeholder="Cerca per codice, cliente, nome, descrizione..."
            style={{ width: "100%", padding: "7px 10px 7px 32px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none" }} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
              <th style={{ textAlign: "left", padding: "10px 16px 10px 24px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 120 }}>N. ordine</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 120 }}>Codice</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 160 }}>Cliente</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 340 }}>Descrizione / Oggetto</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 150 }}>PM</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 110 }}>Apertura</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 110 }}>Chiusura</th>
              <th style={{ textAlign: "center", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 90 }}>Giorni</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 90 }}>Stato</th>
              <th style={{ textAlign: "right", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 120 }}>Valore</th>
              <th style={{ textAlign: "center", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 130 }}>Attività aperte</th>
              <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", width: 130 }}>Salute</th>
              <th style={{ borderBottom: "1px solid #e5e7eb" }}></th>
            </tr>
          </thead>
          <tbody>
            {filtrate.length === 0 ? (
              <tr><td colSpan={12} style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>Nessuna commessa trovata.</td></tr>
            ) : filtrate.map(c => {
              const budget = budgetPerProgetto[c.id];
              const tasks = tasksPerProgetto[c.id] || [];
              const aperte = tasks.filter(t=>t.status!=="completata").length;
              const scadute = tasks.filter(t=>isTaskOverdue(t)).length;
              return (
              <tr key={c.id} onClick={() => onNavigate && onNavigate({ page: "project", projectId: c.id, project: c })}
                style={{ cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}
                onMouseOver={e => e.currentTarget.style.background = "#faf5ff"}
                onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 16px 10px 24px", fontSize: 13, fontWeight: 800, color: "#1e1b4b", whiteSpace: "nowrap" }}>{c.numeroOrdine || <span style={{ color: "#d1d5db", fontWeight: 400 }}>—</span>}</td>
                <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 700, color: "#7c3aed", fontFamily: "monospace" }}>{c.codiceCommessa || "—"}</td>
                <td style={{ padding: "10px 16px", fontSize: 13, color: "#374151" }}>{c.clienteNome || "—"}</td>
                <td style={{ padding: "10px 16px", fontSize: 13, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 1 }}>
                  {c.description || c.name || "—"}
                </td>
                <td style={{ padding: "10px 16px", fontSize: 13, color: "#374151" }}>{c.responsabileNome || "—"}</td>
                <td style={{ padding: "10px 16px", fontSize: 12, color: "#9ca3af" }}>
                  {c.createdAt?.toDate ? c.createdAt.toDate().toLocaleDateString("it-IT") : "—"}
                </td>
                <td style={{ padding: "10px 16px", fontSize: 12, color: c.dataChiusura ? "#059669" : "#d1d5db", fontWeight: c.dataChiusura ? 600 : 400 }}>
                  {c.dataChiusura ? new Date(c.dataChiusura+"T00:00:00").toLocaleDateString("it-IT") : "—"}
                </td>
                <td style={{ padding: "10px 16px", fontSize: 12, textAlign:"center", color: c.dataChiusura ? "#6b7280" : "#7c3aed", fontWeight: c.dataChiusura ? 400 : 700 }}>
                  {(() => { const g = calcGiorniCommessa(c); return g===null ? "—" : g; })()}
                </td>
                <td style={{ padding: "10px 16px" }}>
                  {c.archived ? (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: "#f3f4f6", color: "#6b7280" }}>⚪ Archiviata</span>
                  ) : c.statoCommessa === "chiusa" ? (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: "#ede9fe", color: "#6d28d9" }}>🔒 Chiusa</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: "#d1fae5", color: "#065f46" }}>🟢 Attiva</span>
                  )}
                </td>
                <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "#111827", textAlign:"right" }}>
                  {budget?.importoCommessa ? `€ ${formatValutaItaliana(budget.importoCommessa)}` : <span style={{color:"#d1d5db"}}>—</span>}
                  {variantiDaBudget(budget)>0 && <div style={{fontSize:10.5,fontWeight:600,color:"#7c3aed"}}>+ varianti € {formatValutaItaliana(variantiDaBudget(budget))}</div>}
                </td>
                <td style={{ padding: "10px 16px", fontSize: 12, textAlign:"center" }}>
                  {aperte}{scadute>0 && <span style={{color:"#dc2626",fontWeight:700}}> ({scadute} in ritardo)</span>}
                </td>
                <td style={{ padding: "10px 16px" }}>
                  {(() => {
                    const health = calcHealthScore(tasks);
                    return health.score === null ? (
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>⚪ {health.label}</span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 700, color: health.color }}>{health.emoji} {health.score} · {health.label}</span>
                    );
                  })()}
                </td>
                <td></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
